import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { getColor } from '~/utils/colors'
import { ColorName } from '~/models/Content/Color'

interface AppColorOptions {
  bgColor?: ColorName | string
  themeColor?: ColorName | string
}

const defaultAppColorOptions: Required<AppColorOptions> = {
  bgColor: ColorName.White,
  themeColor: ColorName.White,
}

const appColor = ref(defaultAppColorOptions)
const themeColorEl: HTMLMetaElement | null = document.head.querySelector('meta[name="theme-color"]')

function normalizeColor(color: ColorName | string): string {
  if (typeof color === 'string' && color.startsWith('var(--color-')) {
    return color
  }
  return getColor(color as ColorName)
}

const bgColor = computed(() => normalizeColor(appColor.value.bgColor))
const themeColor = computed(() => normalizeColor(appColor.value.themeColor))

function setAppColor(options: ColorName | string | AppColorOptions) {
  if (typeof options === 'string') {
    appColor.value = { bgColor: options, themeColor: options }
  } else {
    appColor.value = { ...defaultAppColorOptions, ...options }
  }
}

function clearAppColor() {
  appColor.value = defaultAppColorOptions
}

export function useAppColor(options?: ColorName | string | AppColorOptions) {
  if (options) {
    onMounted(() => setAppColor(options))
    onBeforeUnmount(() => clearAppColor())
  }

  return {
    set: setAppColor,
    clear: clearAppColor,
    bgColor,
    themeColor,
  }
}

watch(appColor, ({ themeColor }) => {
  if (themeColorEl) {
    const cssVar = normalizeColor(themeColor)
    const computedColor = getComputedStyle(document.documentElement)
      .getPropertyValue(cssVar.replace('var(', '').replace(')', ''))
    themeColorEl.content = computedColor ? `${computedColor.trim()}` : 'white'
  }
}, { deep: true })
